import React from 'react';
import { segmentToPrint } from 'src/app/chat/segmentKinds';
import { ITranscriptPrintChartPalette } from 'src/app/chat/TranscriptPrintChart';
import {
  describeReplyStatus,
  formatTranscriptTimestamp,
  IChatTranscript,
  ITranscriptFormatOptions,
  transcriptRoleLabel,
} from 'src/app/chat/transcript';

export interface ITranscriptPrintPalette extends ITranscriptPrintChartPalette {
  text: string;
  surface: string;
  fontFamily: string;
  monospaceFontFamily: string;
}

export interface ITranscriptPrintDocumentProps {
  transcript: IChatTranscript;
  palette: ITranscriptPrintPalette;
  options?: ITranscriptFormatOptions;
}

/** Stylesheet for the standalone print document; Mantine's CSS variables do not exist there, so theme values are passed in resolved. */
export const buildTranscriptPrintStyles = (
  palette: ITranscriptPrintPalette,
): string => `
@page { margin: 16mm; }
html, body { margin: 0; padding: 0; }
body {
  font-family: ${palette.fontFamily};
  color: ${palette.text};
  font-size: 11pt;
  line-height: 1.45;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
h1 { font-size: 18pt; margin: 0 0 4pt; }
.meta { list-style: none; margin: 0 0 12pt; padding: 0; color: ${palette.dimmed}; font-size: 9pt; }
.message { border-top: 1px solid ${palette.border}; padding: 8pt 0; }
.message-header { font-weight: 600; font-size: 10pt; color: ${palette.dimmed}; margin: 0 0 4pt; break-after: avoid; }
.user-text { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0 0 6pt; }
.markdown { overflow-wrap: anywhere; }
.markdown p { margin: 0 0 6pt; }
.markdown ul, .markdown ol { margin: 0 0 6pt; padding-left: 18pt; }
/* Collapsed borders straddle the table edge, and Chrome's print preview drops the outer half of the top and left edges; separate borders sit wholly inside each cell. */
.markdown table { border-collapse: separate; border-spacing: 0; margin: 0 0 8pt; }
.markdown thead { display: table-header-group; }
.markdown tr { break-inside: avoid; }
.markdown th, .markdown td { border-right: 1px solid ${palette.border}; border-bottom: 1px solid ${palette.border}; padding: 3pt 6pt; text-align: left; vertical-align: top; }
.markdown th { border-top: 1px solid ${palette.border}; }
.markdown tr > :first-child { border-left: 1px solid ${palette.border}; }
.markdown pre { white-space: pre-wrap; background: ${palette.surface}; border-radius: 4pt; padding: 6pt; }
.markdown code { font-family: ${palette.monospaceFontFamily}; font-size: 0.9em; }
.markdown img { max-width: 100%; }
.queries-label { font-size: 9pt; font-weight: 600; color: ${palette.dimmed}; margin: 4pt 0 0; }
.queries { font-size: 9pt; color: ${palette.dimmed}; margin: 0 0 6pt; padding-left: 16pt; }
.queries code { font-family: ${palette.monospaceFontFamily}; }
.chart { margin: 0 0 8pt; }
.chart-figure { break-inside: avoid; margin: 0 0 6pt; }
.chart-title { font-weight: 600; margin: 0 0 4pt; break-after: avoid; }
.chart svg { display: block; }
.chart .swatch { display: inline-block; flex-shrink: 0; }
.chart-legend { display: flex; flex-wrap: wrap; gap: 2pt 12pt; list-style: none; margin: 4pt 0 0; padding: 0; font-size: 9pt; }
.chart-legend li { display: flex; align-items: center; gap: 4pt; }
.pie-legend { list-style: none; margin: 6pt 0 0; padding: 0; width: 320px; font-size: 9pt; }
.pie-legend li { display: flex; justify-content: space-between; gap: 12pt; break-inside: avoid; }
.pie-legend .legend-name { display: flex; align-items: center; gap: 4pt; min-width: 0; overflow-wrap: anywhere; }
.data-table { border-collapse: separate; border-spacing: 0; }
.data-table thead { display: table-header-group; }
.data-table tr { break-inside: avoid; }
.data-table th, .data-table td { border-right: 1px solid ${palette.border}; border-bottom: 1px solid ${palette.border}; padding: 3pt 6pt; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
.data-table th { border-top: 1px solid ${palette.border}; }
.data-table tr > :first-child { border-left: 1px solid ${palette.border}; }
.data-table .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; overflow-wrap: normal; }
.table { margin: 0 0 8pt; }
.table-title { font-weight: 600; margin: 0 0 4pt; break-after: avoid; }
.memory { font-size: 9pt; margin: 0 0 6pt; }
.memory-label { font-style: italic; color: ${palette.dimmed}; }
.note { font-style: italic; font-size: 9pt; color: ${palette.dimmed}; margin: 4pt 0 0; }
`;

/** @registry Plain-HTML rendering of a whole chat transcript (assistant markdown via react-markdown + GFM, charts drawn at a fixed print size above their data tables, tables in full with a truncation note, memory notes as one line each, `[memory:N]` markers removed, entity links as absolute app URLs given `appOrigin`) for the isolated print iframe; not for use inside the Mantine tree. */
export const TranscriptPrintDocument: React.FC<
  ITranscriptPrintDocumentProps
> = ({ transcript, palette, options = {} }) => (
  <main>
    <h1>{transcript.title}</h1>
    <ul className="meta">
      <li>
        Exported {formatTranscriptTimestamp(transcript.exportedAt, options)}
      </li>
      {transcript.startedAt && (
        <li>
          Started {formatTranscriptTimestamp(transcript.startedAt, options)}
        </li>
      )}
      {transcript.jobLabel && <li>Job: {transcript.jobLabel}</li>}
    </ul>
    {transcript.messages.map((message) => {
      const status = describeReplyStatus(message);
      return (
        <article key={message.id} className="message" data-role={message.role}>
          <p className="message-header">
            {transcriptRoleLabel(message.role)} ·{' '}
            {formatTranscriptTimestamp(message.createdAt, options)}
          </p>
          {message.segments.map((segment, index) => (
            <React.Fragment key={index}>
              {segmentToPrint(segment, {
                role: message.role,
                options,
                palette,
              })}
            </React.Fragment>
          ))}
          {status && <p className="note">{status}</p>}
        </article>
      );
    })}
  </main>
);
